<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import Icon from './Icon.vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    label: string;
    type?: string;
    placeholder?: string;
    error?: string;
    hint?: string;
    autocomplete?: string;
    inputmode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'decimal';
    maxlength?: number;
    required?: boolean;
    disabled?: boolean;
  }>(),
  {
    type: 'text',
    placeholder: '',
    error: '',
    hint: '',
    autocomplete: 'off',
    inputmode: 'text',
    maxlength: 512,
    required: false,
    disabled: false,
  },
);

defineEmits<{ 'update:modelValue': [value: string] }>();

const inputId = useId();
const messageId = computed(() => `${inputId}-msg`);
const hasError = computed(() => props.error.length > 0);
const describedBy = computed(() =>
  hasError.value || props.hint.length > 0 ? messageId.value : undefined,
);

const revealed = ref(false);
const isPassword = computed(() => props.type === 'password');
const effectiveType = computed(() =>
  isPassword.value ? (revealed.value ? 'text' : 'password') : props.type,
);
</script>

<template>
  <div class="field">
    <label :for="inputId" class="field__label">
      {{ props.label }}
      <span v-if="props.required" aria-hidden="true" class="field__required">*</span>
    </label>
    <div class="field__wrap">
      <input
        :id="inputId"
        :value="props.modelValue"
        :type="effectiveType"
        :placeholder="props.placeholder"
        :autocomplete="props.autocomplete"
        :inputmode="props.inputmode"
        :maxlength="props.maxlength"
        :required="props.required"
        :disabled="props.disabled"
        :aria-invalid="hasError"
        :aria-describedby="describedBy"
        :class="['field__input', { 'field__input--error': hasError, 'field__input--reveal': isPassword }]"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />
      <button
        v-if="isPassword"
        type="button"
        class="field__reveal"
        :aria-label="revealed ? 'Hide value' : 'Show value'"
        :aria-pressed="revealed"
        @click="revealed = !revealed"
      >
        <Icon :name="revealed ? 'eye-off' : 'eye'" :size="18" />
      </button>
    </div>

    <p v-if="hasError" :id="messageId" class="field__message field__message--error" role="alert">
      {{ props.error }}
    </p>
    <p v-else-if="props.hint.length > 0" :id="messageId" class="field__message">
      {{ props.hint }}
    </p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.field__label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}

.field__required {
  color: var(--danger-500);
}

.field__wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.field__input {
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  transition:
    border-color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.field__input::placeholder {
  color: var(--text-muted);
}

.field__input:hover:not(:disabled) {
  border-color: var(--border-strong);
}

.field__input:focus {
  outline: none;
  border-color: var(--brand-500);
  box-shadow: 0 0 0 3px rgb(139 92 246 / 0.16);
}

.field__input--error {
  border-color: var(--danger-500);
}

.field__input--error:focus {
  box-shadow: 0 0 0 3px rgb(239 68 68 / 0.16);
}

.field__input:disabled {
  background: var(--surface-sunken);
  cursor: not-allowed;
  opacity: 0.7;
}

.field__input--reveal {
  padding-right: calc(var(--space-4) + 28px);
}

.field__reveal {
  position: absolute;
  right: var(--space-2);
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out);
}

.field__reveal:hover {
  color: var(--text-primary);
}

.field__message {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.field__message--error {
  color: var(--danger-500);
}
</style>
